/**
 * Bulk API handler for B2M Tracker
 */
import { collectPixelParams } from '../utils/pixel-params.js';
import { shouldDropEventParam } from '../utils/property-scope.js';

export default class BulkAPI {
  constructor(tracker) {
    this.tracker = tracker;
  }
  
  /**
   * Helper to create event property objects
   * @param {string} key - Property key
   * @param {any} value - Property value
   * @returns {Object} Property object
   */
  createEventProperty(key, value) {
    if (value === undefined || value === null) return null;
    return { key, value: String(value) };
  }

  /**
   * Resolve the app_identifier value written into user_params.
   *
   * Priority:
   *  1) Manual override from integration (`appIdentifier` / `data-app-identifier`)
   *     — free-form name, e.g. "checkout-app". Used when set (non-empty).
   *  2) Fallback (default for existing sites that never set it):
   *     - iframe → host page domain
   *     - else → window.location.hostname
   *
   * Empty / missing override must NOT change behaviour for current integrations.
   * @returns {string|null}
   */
  getAppIdentifier() {
    const configured = this.tracker.config.appIdentifier;
    if (typeof configured === 'string' && configured.length > 0) {
      return configured;
    }

    const b2mTracker = this.tracker.b2mTracker;
    if (b2mTracker && b2mTracker._isInIframe()) {
      const hostDomain = b2mTracker._getHostPageDomain();
      if (hostDomain && hostDomain !== 'unknown') return hostDomain;
    }

    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
      return window.location.hostname;
    }
    return null;
  }

  /**
   * Custom user properties set through setUserProperties(). They are
   * persisted in `b2m_user_profile`, so we stamp them onto every event's
   * user_params — this is how attributes like company_id / email /
   * application_id travel with all events, not only the one-off $identify.
   * @returns {Array<{key:string,value:string}>}
   */
  getProfileProperties() {
    const profile = this.tracker.storage.getItem('b2m_user_profile') || {};
    return Object.entries(profile).map(
      ([key, value]) => this.createEventProperty(key, value)
    );
  }

  /**
   * Build user_params for a queued event.
   *
   * Native: SDK device + attribution + profile (unchanged).
   *
   * Datalayer / hybrid (user + device props):
   *   1) Start from native SDK defaults (device, pixels, attribution, profile)
   *   2) Overlay dataLayer-harvested props — DL wins on every key that is present
   *   3) Keys missing from DL stay as SDK values (native-style completion)
   *
   * Events themselves are separate: datalayer never invents events; hybrid
   * auto-completes missing registered events via shouldEmitSelfEvent.
   *
   * @param {object} event
   * @param {object} browserInfo
   * @param {object} pixelParams
   * @returns {Array<{key:string,value:string}>}
   */
  buildUserParams(event, browserInfo, pixelParams) {
    const exclusive = this.tracker.config.isExclusiveDataLayerCollection();
    const dlProps = exclusive
      ? (this.tracker.b2mTracker?.getDataLayerUserProps?.() || {})
      : {};

    const sdkUserId = event._userId || this.tracker.userId.getId();
    // DL login id may override the user_params / envelope user_id when present
    const resolvedUserId = (exclusive && dlProps.user_id != null && dlProps.user_id !== '')
      ? String(dlProps.user_id)
      : sdkUserId;

    // Parsed device identity (os/browser/device_type) — user-level copies of
    // the auto-collected event fields so the central user table doesn't have
    // to parse the raw user_agent (UA freezing makes that increasingly wrong).
    const autoProps = this.tracker.browserInfo.getAutoProperties();

    const sdkParams = [
      this.createEventProperty('user_id', resolvedUserId),
      this.createEventProperty('app_identifier', this.getAppIdentifier()),
      this.createEventProperty('fingerprint_id', this.tracker.userId.getFingerprintId()),
      // Deterministic device-CLASS hash (identical devices share it) — a
      // server-side stitching hint, never an identity. Resolves async in
      // init(); events assembled before that simply omit it.
      this.tracker.userId.getDeviceClassHash() &&
        this.createEventProperty('device_class_hash', this.tracker.userId.getDeviceClassHash()),
      (event._userId ? event._idIsTemporary : this.tracker.userId.isIdTemporary()) &&
        this.createEventProperty('id_status', 'temporary'),
      autoProps.os && this.createEventProperty('os', autoProps.os),
      autoProps.os_version && this.createEventProperty('os_version', autoProps.os_version),
      autoProps.browser && this.createEventProperty('browser', autoProps.browser),
      autoProps.browser_version && this.createEventProperty('browser_version', autoProps.browser_version),
      autoProps.device_type && this.createEventProperty('device_type', autoProps.device_type),
      this.createEventProperty('user_agent', browserInfo.user_agent),
      this.createEventProperty('language', browserInfo.language),
      // SDK surface: "Web" (desktop) / "M-Web" (mobile|tablet browser).
      // NOT navigator.platform (Win32/MacIntel) and NOT native iOS/Android.
      this.createEventProperty('platform', this.tracker.browserInfo.getSdkPlatform()),
      this.createEventProperty('screen_resolution', browserInfo.screen.split('x').slice(0, 2).join('x')),
      this.createEventProperty('color_depth', browserInfo.screen.split('x')[2]),
      this.createEventProperty('timezone', browserInfo.timezone),
      this.createEventProperty('timezone_offset', browserInfo.timezone_offset),
      browserInfo.hardware_concurrency && this.createEventProperty('hardware_concurrency', browserInfo.hardware_concurrency),
      browserInfo.device_memory && this.createEventProperty('device_memory', browserInfo.device_memory),
      this.createEventProperty('is_touch_capable', browserInfo.touch_support),
      browserInfo.webgl_vendor_renderer && this.createEventProperty('webgl_vendor', browserInfo.webgl_vendor_renderer),
      browserInfo.hash_of_canvas_fingerprint && this.createEventProperty('canvas_hash', browserInfo.hash_of_canvas_fingerprint),
      pixelParams.$fbp && this.createEventProperty('$fbp', pixelParams.$fbp),
      pixelParams.$fbc && this.createEventProperty('$fbc', pixelParams.$fbc),
      pixelParams.$gclid && this.createEventProperty('$gclid', pixelParams.$gclid),
      pixelParams.$ga_client_id && this.createEventProperty('$ga_client_id', pixelParams.$ga_client_id),
      ...this.tracker.getAttributionUserParams().map(p => this.createEventProperty(p.key, p.value)),
      ...this.getProfileProperties()
    ].filter(Boolean);

    if (!exclusive || !dlProps || Object.keys(dlProps).length === 0) {
      return sdkParams;
    }

    // Merge: SDK base → DL overlay (DL wins on every overlapping key, including device).
    const byKey = {};
    sdkParams.forEach((p) => {
      if (p && p.key) byKey[p.key] = p;
    });

    Object.keys(dlProps).forEach((key) => {
      const val = dlProps[key];
      if (val === undefined || val === null || val === '') return;
      // `platform` is the SDK surface (Web / M-Web) — never let dataLayer OS/arch
      // values (Win32, MacIntel, …) overwrite it. OS belongs in `os`.
      if (key === 'platform') return;
      byKey[key] = this.createEventProperty(key, val);
    });

    // Guarantee SDK surface even if something else cleared it.
    byKey.platform = this.createEventProperty('platform', this.tracker.browserInfo.getSdkPlatform());

    return Object.values(byKey).filter(Boolean);
  }

  /**
   * Resolve envelope user_id for an event (DL override in exclusive modes).
   * @param {object} event
   * @returns {string}
   */
  resolveEnvelopeUserId(event) {
    const sdkUserId = event._userId || this.tracker.userId.getId();
    if (!this.tracker.config.isExclusiveDataLayerCollection()) return sdkUserId;
    const dlProps = this.tracker.b2mTracker?.getDataLayerUserProps?.() || {};
    if (dlProps.user_id != null && dlProps.user_id !== '') return String(dlProps.user_id);
    return sdkUserId;
  }

  /**
   * Get or generate a session ID - maintains consistency across requests
   * @returns {string} Session ID
   */
  generateSessionId() {
    // Check if we have a valid existing session
    const existingSessionId = this.tracker.storage.getSessionId();
    if (existingSessionId && !this.tracker.storage.isSessionExpired(this.tracker.config.sessionTimeout)) {
      return existingSessionId;
    }
    
    // Generate new session
    const timestamp = Date.now();
    const randomPart = Math.floor(Math.random() * 1000000);
    const userIdFragment = this.tracker.userId.getId().substring(0, 8);
    
    let sessionId;
    
    // Use custom session parameters if available
    if (this.tracker.config.sessionParams) {
      const params = this.tracker.config.sessionParams;
      sessionId = params.prefix || 'session';
      
      if (params.includeTimestamp) {
        sessionId += `_${timestamp}`;
      }
      
      if (params.includeUserId) {
        sessionId += `_${userIdFragment}`;
      }
      
      if (params.includeRandom) {
        sessionId += `_${randomPart}`;
      }
    } else {
      // Default format
      sessionId = `session_${timestamp}_${userIdFragment}_${randomPart}`;
    }
    
    // Store the new session
    this.tracker.storage.setSessionId(sessionId);
    this.tracker.storage.setSessionStartTime(timestamp);
    
    return sessionId;
  }
  
  
  sendEvents(useBeacon = false) {
    const queue = this.tracker.storage.getBulkQueue();
    
    if (queue.length === 0) {
      this.tracker.logger.log("No bulk events to send");
      return;
    }
    
    // If offline, don't try to send (unless using beacon on page exit)
    if (!this.tracker.isOnline && !useBeacon) {
      this.tracker.logger.log("Device is offline, bulk events will be sent when online");
      return;
    }

    // Re-entrancy guard: the queue is only cleared after the HTTP response, so a
    // second sendEvents() before that response (e.g. init-flush racing with
    // giveConsent() on a slow network) would re-transmit the same batch. Skip
    // while a normal send is in flight; beacon (unload) sends bypass this.
    if (!useBeacon && this._sendInFlight) {
      this.tracker.logger.log("Bulk send already in flight, skipping duplicate");
      return;
    }

    // Get browser details for fingerprinting
    const browserInfo = this.tracker.browserInfo.getInfo();

    // Marketing pixel identifiers (_fbp, _fbc, gclid, _ga client id).
    // Read fresh per batch — cookies set by Meta Pixel/GA may appear after init.
    const pixelParams = collectPixelParams(this.tracker.storage);

    // Create a session ID for this batch of events
    const sessionId = this.generateSessionId();
    
    // ANALYTICS-6897: record every value's shape across the WHOLE batch before
    // converting, so the first event of a batch gets the same dtype as the rest.
    const dtypeRegistry = this.tracker.config.autoPropertyTypes ? this.tracker.propertyDtypes : null;
    if (dtypeRegistry) {
      try {
        const eu = this.tracker.eventUtils;
        const declared = this.tracker.config.propertyTypes;
        queue.forEach((ev) => Object.keys(ev).forEach((key) => {
          if (key.startsWith('_') || key === 'event') return;
          if (shouldDropEventParam(key, ev.event)) return;
          if (key === 'items' && Array.isArray(ev[key])) return;
          dtypeRegistry.observe(key, eu.enforceType(key, eu.coerceValue(ev[key]), declared), declared);
        }));
      } catch (e) { /* never block a send on dtype bookkeeping */ }
    }

    // Transform queue to match the custom API format
    const formattedEvents = queue.map((event, index) => {
      // Determine the data source
      const dataSource = queue[index];
      const dataSource_name = queue[index].interceptor || "custom";
      
      // Create event_params array from all properties in the event
      const eventParams = [];
      
      // Map all properties from the event object to event_params array
      Object.keys(event).forEach(key => {
        // Skip internal properties that start with underscore like _timestamp
        // Skip 'event' key as it's already sent as event_name
        if (key.startsWith('_') || key === 'event') return;
        // User-level keys (language, utm_*, referrer, device, …) travel in
        // user_params only — never as event_params (ANALYTICS-6725).
        if (shouldDropEventParam(key, event.event)) return;

        const val = event[key];

        // Flatten items array — each item's properties become separate params (item_0_item_id, etc.)
        if (key === 'items' && Array.isArray(val)) {
          val.forEach((item, index) => {
            const itemObj = typeof item === 'string' ? (() => { try { return JSON.parse(item); } catch(e) { return null; } })() : item;
            if (itemObj && typeof itemObj === 'object') {
              Object.keys(itemObj).forEach(itemKey => {
                const coerced = this.tracker.eventUtils.coerceValue(itemObj[itemKey]);
                if (coerced !== null) {
                  eventParams.push({ key: `item_${index}_${itemKey}`, value: coerced });
                }
              });
            }
          });
          return;
        }

        let coerced = this.tracker.eventUtils.coerceValue(val);
        coerced = this.tracker.eventUtils.enforceType(key, coerced, this.tracker.config.propertyTypes);
        // Undeclared keys: one stable dtype per property name (ANALYTICS-6897).
        if (this.tracker.propertyDtypes && this.tracker.config.autoPropertyTypes) {
          coerced = this.tracker.propertyDtypes.apply(key, coerced, this.tracker.config.propertyTypes);
        }
        if (coerced !== null) {
          eventParams.push({ key, value: coerced });
        }
      });
      
      const userId = this.resolveEnvelopeUserId(event);

      return {
        event_name: event.event || "bulk_event",
        event_date: event._timestamp || new Date().toISOString(),
        event_timestamp: Math.floor(Date.now() / 1000),
        created_at: Math.floor(Date.now() / 1000), // Second timestamp
        event_params: eventParams,
        session_id: event._sessionId || sessionId,
        user_first_timestamp: this.tracker.storage.getFirstTimestamp(),
        user_id: userId,
        user_params: this.buildUserParams(event, browserInfo, pixelParams)
      };
    });
    
    const payload = JSON.stringify(formattedEvents);
    
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": this.tracker.config.apiKey
    };
    
    // Use sendBeacon for unload events (page close, tab close, navigation)
    if (useBeacon) {
      try {
        // First try fetch with keepalive (supports custom headers unlike sendBeacon)
        if (typeof fetch !== 'undefined') {
          try {
            fetch(this.tracker.config.apiUrl, {
              method: "POST",
              headers: headers,
              body: payload,
              keepalive: true // Allows request to outlive the page
            }).catch(() => {}); // Silently fail - page is closing anyway
            
            this.tracker.logger.log("Bulk events sent via fetch keepalive");
            this.tracker.storage.removeFromBulkQueue(queue.length);
            return;
          } catch (fetchError) {
            // fetch with keepalive failed, fall through to sendBeacon
            this.tracker.logger.log("fetch keepalive failed, trying sendBeacon");
          }
        }
        
        // Fallback to sendBeacon (no custom headers, but reliable on page close)
        if (navigator.sendBeacon) {
          // Append api key to URL since sendBeacon can't send custom headers
          const beaconUrl = new URL(this.tracker.config.apiUrl);
          beaconUrl.searchParams.set('api_key', this.tracker.config.apiKey);
          
          const blob = new Blob([payload], { type: 'application/json' });
          const success = navigator.sendBeacon(beaconUrl.toString(), blob);
          
          if (success) {
            this.tracker.logger.log("Bulk events sent successfully via Beacon");
            this.tracker.storage.removeFromBulkQueue(queue.length);
          } else {
            this.tracker.logger.error("Failed to send bulk events via Beacon");
          }
        }
      } catch (e) {
        this.tracker.logger.error("Error sending bulk events via Beacon:", e);
      }
      return;
    }
    
    // Otherwise use fetch API
    this._sendInFlight = true;
    fetch(this.tracker.config.apiUrl, {
      method: "POST",
      headers: headers,
      body: payload
    })
    .then(response => {
      if (!response.ok) {
        throw new Error("Server returned " + response.status);
      }
      
      // Check if response has content
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return response.json().catch(() => {
          // If JSON parsing fails, return empty object
          this.tracker.logger.log("Response is not valid JSON, but status is OK");
          return {};
        });
      } else {
        // No JSON content, return empty object
        this.tracker.logger.log("Response has no JSON content, but status is OK");
        return Promise.resolve({});
      }
    })
    .then(data => {
      this.tracker.logger.log("Bulk events sent successfully:", data);
      // Remove only the transmitted batch — events queued while this request
      // was in flight must survive for the next send.
      this.tracker.storage.removeFromBulkQueue(queue.length);
      this.tracker.retryCount = 0;
    })
    .catch(error => {
      this.tracker.logger.error("Error sending bulk events:", error);
      
      // Check if this is a permanent error (4xx errors except 429)
      const isPermanentError = error.message && (
        error.message.includes("401") ||  // Unauthorized
        error.message.includes("403") ||  // Forbidden
        error.message.includes("404") ||  // Not Found
        error.message.includes("400")     // Bad Request
      );
      
      // Clear queue for permanent errors only
      if (isPermanentError) {
        this.tracker.logger.log("Dropping rejected batch due to permanent error");
        this.tracker.storage.removeFromBulkQueue(queue.length);
        this.tracker.retryCount = 0;
        return;
      }
      
      // Implement retry logic for temporary errors only
      if (this.tracker.retryCount < this.tracker.config.maxRetries) {
        this.tracker.retryCount++;
        
        // Exponential backoff: 2s, 4s, 8s, 16s… with jitter
        const baseDelay = Math.min(2000 * Math.pow(2, this.tracker.retryCount - 1), 60000);
        const jitter = Math.random() * 1000;
        const delay = baseDelay + jitter;
        
        setTimeout(() => {
          this.tracker.logger.log(`Retrying bulk event send. Attempt ${this.tracker.retryCount} (delay: ${Math.round(delay)}ms)`);
          this.sendEvents();
        }, delay);
      } else {
        // Max retries reached - clear queue to prevent infinite accumulation
        this.tracker.logger.log("Max retries reached, dropping unsendable batch");
        this.tracker.storage.removeFromBulkQueue(queue.length);
        this.tracker.retryCount = 0;
      }
    })
    .finally(() => { this._sendInFlight = false; });
  }
}
